import React, { useState, useEffect } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../config/firebase';
import { updateProfile } from 'firebase/auth';
import './UserSettings.css';

const UserSettings = () => {
    const [user] = useAuthState(auth);
    const [formData, setFormData] = useState({
        displayName: '',
        email: '',
        phone: '',
        notifications: {
            email: true,
            sms: false
        }
    });
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        if (user) {
            const fetchUserDetails = async () => {
                try {
                    const response = await fetch(`/api/users/${user.uid}`);
                    const data = await response.json();
                    setFormData({
                        displayName: user.displayName || '',
                        email: user.email || '',
                        phone: data.phone || '',
                        notifications: data.notifications || {
                            email: true,
                            sms: false
                        }
                    });
                    setLoading(false);
                } catch (error) {
                    console.error('Error fetching user details:', error);
                    setLoading(false);
                }
            };

            fetchUserDetails();
        }
    }, [user]);

    const handleInputChange = (e) => {
        const { name, value, checked } = e.target;
        
        if (name.startsWith('notifications-')) {
            const notificationType = name.replace('notifications-', '');
            setFormData(prev => ({
                ...prev,
                notifications: {
                    ...prev.notifications,
                    [notificationType]: checked
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage({ type: '', text: '' });
        
        try {
            // Update Firebase display name
            await updateProfile(auth.currentUser, {
                displayName: formData.displayName
            });

            // Update backend user details
            const response = await fetch(`/api/users/${user.uid}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    phone: formData.phone,
                    notifications: formData.notifications
                })
            });

            if (!response.ok) throw new Error('Failed to update profile');

            setMessage({ type: 'success', text: 'Profile updated successfully' });
        } catch (error) {
            console.error('Error updating profile:', error);
            setMessage({ 
                type: 'error', 
                text: 'Failed to update profile. Please try again.' 
            });
        }
    };

    return (
        <div className="user-settings-container">
            <h1>User Settings</h1>
            {loading && <div className="loading-spinner">Loading...</div>}
            {message.text && <div className={`message ${message.type}`}>{message.text}</div>}
            <form onSubmit={handleSubmit}>
                <div className="form-group">
                    <label htmlFor="displayName">Display Name</label>
                    <input
                        type="text"
                        id="displayName"
                        name="displayName"
                        value={formData.displayName}
                        onChange={handleInputChange}
                        required
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="phone">Phone</label>
                    <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="notifications-email">Email Notifications</label>
                    <input
                        type="checkbox"
                        id="notifications-email"
                        name="notifications-email"
                        checked={formData.notifications.email}
                        onChange={handleInputChange}
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="notifications-sms">SMS Notifications</label>
                    <input
                        type="checkbox"
                        id="notifications-sms"
                        name="notifications-sms"
                        checked={formData.notifications.sms}
                        onChange={handleInputChange}
                    />
                </div>
                <button type="submit">Save Changes</button>
            </form>
        </div>
    );
};

export default UserSettings; 